import * as React from 'react';
import { useNavigate } from 'react-router';
import type { ExtensionRegistration, PiletApi } from '@chess/platform-web';
import {
  DASHBOARD_BAND,
  DASHBOARD_CARD,
  DASHBOARD_PARTS,
  DASHBOARD_SECTION,
  frameOf,
  type BandParams,
  type CardMeta,
  type SectionParams,
} from '@chess/api-dashboard';
import { DashboardPartsContext, type DashboardParts, type DashboardSectionProps } from '@chess/api-dashboard/react';
import { DashboardSection } from './DashboardSection.tsx';
import { DashboardBand } from './DashboardBand.tsx';
import { DashboardCard } from './DashboardCard.tsx';
import { byOrder } from './layout.ts';
import './dashboard.css';

// The dashboard, for every screen that shows one: its sections, its bands
// and the frame of its cards (2026-10-10; home-section's `home.section` and
// `home.band` now render these).
//
//   app.registerExtension('openings.sections', () => (
//     <app.Extension name="dashboard.section" params={{ title: 'History', slot: 'home.section.openings' }} />
//   ), { order: 5 });
//   registerCard(app, 'home.section.openings', MostPlayedBody, { order: 10, title: 'Most played', help: 'most-played' });
//
// A section renders its slot's contributions by `order`; one registered with
// card metadata (`registerCard`) is drawn in the frame, one without (or with
// `frame: 'none'`) as it is, so old-style cards and composers stand beside
// framed ones.

/** Draws the frame around a contribution that asks for one (`defaults.card`); the rest pass through. */
const frame = (node: React.ReactNode, registration: ExtensionRegistration): React.ReactNode => {
  const card = frameOf(registration.defaults);
  return card ? <DashboardCard {...card}>{node}</DashboardCard> : node;
};

export function setup(app: PiletApi) {
  /** A section as a composer draws it: the screen link opened inside the app. */
  const Section: React.FC<DashboardSectionProps> = ({ screen, ...rest }) => {
    const navigate = useNavigate();
    const link = screen ? { href: screen.to, open: () => navigate(screen.to), icon: screen.icon, label: screen.label } : undefined;
    return <DashboardSection {...rest} screen={link} />;
  };
  const parts: DashboardParts = { Card: DashboardCard, Section };

  const SlotSection: React.FC<{ params: SectionParams }> = ({ params }) => {
    const { slot, empty, after, ...rest } = params;
    return (
      <Section {...rest} after={after ? <app.Extension name={after} order={byOrder} wrap={frame} /> : undefined}>
        {slot && (
          <app.Extension
            name={slot}
            order={byOrder}
            wrap={frame}
            empty={empty ? () => <p className="panel-empty home-section-empty">{empty}</p> : undefined}
          />
        )}
      </Section>
    );
  };

  app.registerExtension<SectionParams>(DASHBOARD_SECTION, ({ params }) => (
    <DashboardPartsContext.Provider value={parts}>
      <SlotSection params={params} />
    </DashboardPartsContext.Provider>
  ));
  // A band of sections on a tint under a scope label: the dashboard screens'
  // 30-day aggregates, above the game bar and the selected game's sections.
  app.registerExtension<BandParams>(DASHBOARD_BAND, ({ params }) => (
    <DashboardBand label={params.label ?? 'Last 30 days'} continuous={params.continuous}>
      <app.Extension name={params.slot} order={byOrder} />
    </DashboardBand>
  ));
  // One frame around its children, for content that is not a slot's contribution.
  app.registerExtension<CardMeta>(DASHBOARD_CARD, ({ params, children }) => <DashboardCard {...params}>{children}</DashboardCard>);
  // The real parts for a composer's `useDashboardParts`, around whatever it wraps.
  app.registerExtension(DASHBOARD_PARTS, ({ children }) => <DashboardPartsContext.Provider value={parts}>{children}</DashboardPartsContext.Provider>);
}
